import React from 'react';
import { Check, X, DollarSign, Users, MessageCircle, Zap, ArrowRight } from 'lucide-react';
import { useSEO } from '../utils/seo';

const CompareZendesk = () => {
  useSEO({
    title: 'TicketDesk vs Zendesk — Affordable Alternative for Small Business',
    description: 'Compare TicketDesk vs Zendesk. Get unlimited users, Gmail & WhatsApp integration, and Trustpilot alerts for $29/mo — no per-seat pricing.',
    path: '/compare/zendesk',
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">TicketDesk</span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/" className="text-gray-600 hover:text-gray-900">Home</a>
              <a href="/register" className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700">
                Start Free Trial
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              TicketDesk vs Zendesk
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get the same multi-channel support features at 10x less cost. No per-seat fees, no enterprise complexity.
            </p>
          </div>

          {/* Quick Comparison Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Zendesk Card */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Zendesk Suite</h3>
                <div className="text-4xl font-bold text-gray-900 mb-2">$115</div>
                <div className="text-gray-600">per user/month</div>
                <div className="mt-2 text-sm text-red-600 font-medium">5 users = $575/month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Multi-channel support</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">AI-powered features</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Complex setup (weeks)</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Expensive per-user pricing</span>
                </div>
                <div className="flex items-start">
                  <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Built for enterprise</span>
                </div>
              </div>
            </div>

            {/* TicketDesk Card */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 shadow-lg border-2 border-purple-500 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  10x More Affordable
                </span>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">TicketDesk</h3>
                <div className="text-4xl font-bold text-purple-600 mb-2">$29</div>
                <div className="text-gray-600">flat rate/month</div>
                <div className="mt-2 text-sm text-green-600 font-medium">Unlimited users = $29/month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Multi-channel support</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">AI-powered responses</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Setup in 2 minutes</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">No per-user fees</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Built for small teams</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Feature-by-Feature Comparison
          </h2>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Zendesk Suite</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-purple-600">TicketDesk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Monthly Price (5 users)</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-700">$575/month</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">$29/month</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Per-User Pricing</td>
                  <td className="px-6 py-4 text-center">
                    <X className="w-5 h-5 text-red-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Email Support</td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Social Media (Facebook)</td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Review Management (Trustpilot)</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Add-on</td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">AI Response Suggestions</td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Setup Time</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-700">2-4 weeks</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">2 minutes</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Multiple Workspaces</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Enterprise only</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">$79/month</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Contract Required</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-700">Annual</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">None</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">Implementation Support</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-700">Extra cost</td>
                  <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">Built-in</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cost Breakdown */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Real Cost Over Time
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="text-sm text-gray-600 mb-2">First Year</div>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Zendesk (5 users)</div>
                  <div className="text-3xl font-bold text-gray-900">$6,900</div>
                </div>
                <div className="border-t pt-4">
                  <div className="text-sm text-purple-600 mb-1">TicketDesk (unlimited)</div>
                  <div className="text-3xl font-bold text-purple-600">$348</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-sm text-green-800 font-semibold">You save $6,552</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="text-sm text-gray-600 mb-2">Year Two</div>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Zendesk (5 users)</div>
                  <div className="text-3xl font-bold text-gray-900">$13,800</div>
                </div>
                <div className="border-t pt-4">
                  <div className="text-sm text-purple-600 mb-1">TicketDesk (unlimited)</div>
                  <div className="text-3xl font-bold text-purple-600">$696</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-sm text-green-800 font-semibold">You save $13,104</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="text-sm text-gray-600 mb-2">Three Years</div>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Zendesk (5 users)</div>
                  <div className="text-3xl font-bold text-gray-900">$20,700</div>
                </div>
                <div className="border-t pt-4">
                  <div className="text-sm text-purple-600 mb-1">TicketDesk (unlimited)</div>
                  <div className="text-3xl font-bold text-purple-600">$1,044</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-sm text-green-800 font-semibold">You save $19,656</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Differences */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Teams Switch from Zendesk
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Predictable Pricing</h3>
              <p className="text-gray-700 mb-4">
                No surprises. Pay one flat rate whether you have 2 users or 20. No hidden fees for adding team members.
              </p>
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="text-sm text-gray-600">"We went from $800/month on Zendesk to $29/month on TicketDesk"</div>
                <div className="text-xs text-gray-500 mt-2">- Sarah K., E-commerce</div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Simple Setup</h3>
              <p className="text-gray-700 mb-4">
                Connect your Gmail in 60 seconds. No consultants, no training sessions, no complex configuration.
              </p>
              <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
                <div className="text-sm text-gray-600">"Set up in 5 minutes vs 3 weeks with Zendesk"</div>
                <div className="text-xs text-gray-500 mt-2">- Mike R., SaaS Startup</div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Built for Small Teams</h3>
              <p className="text-gray-700 mb-4">
                Zendesk is enterprise software. TicketDesk is designed for small businesses who need powerful features without complexity.
              </p>
              <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500">
                <div className="text-sm text-gray-600">"Finally, support software that doesn't feel overwhelming"</div>
                <div className="text-xs text-gray-500 mt-2">- Lisa C., Consulting</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Migration Guide */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Switching is Easy
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Most teams migrate from Zendesk in under an hour
          </p>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Your Free Trial</h3>
                <p className="text-gray-700">Sign up for TicketDesk in 30 seconds. No credit card required.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect Your Channels</h3>
                <p className="text-gray-700">Link Gmail, Facebook, and Trustpilot. Takes about 2 minutes total.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Invite Your Team</h3>
                <p className="text-gray-700">Add unlimited team members at no extra cost. They'll get instant access.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Supporting Customers</h3>
                <p className="text-gray-700">Run both systems in parallel if you want. Cancel Zendesk when you're ready.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Common Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I import my existing tickets from Zendesk?</h3>
              <p className="text-gray-700">Currently we don't offer automated imports, but you can keep Zendesk read-only for historical reference while using TicketDesk for all new tickets.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What happens to my Zendesk data?</h3>
              <p className="text-gray-700">Your Zendesk data stays in Zendesk. You can export it before canceling or keep your account in a downgraded state for archival purposes.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How does TicketDesk compare on features?</h3>
              <p className="text-gray-700">We focus on the core features small businesses actually use: multi-channel inbox, AI assistance, team collaboration. We skip the enterprise complexity you're paying for but not using.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What if I need more advanced features later?</h3>
              <p className="text-gray-700">We're constantly adding features. If you outgrow TicketDesk, you can always move back to enterprise tools. But most small businesses find we have everything they need.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Save $6,000+ Per Year?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of teams who switched from Zendesk to TicketDesk
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/register" 
              className="bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <a 
              href="/" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors"
            >
              Learn More
            </a>
          </div>
          <p className="text-sm opacity-75 mt-6">
            No credit card required • Setup in 2 minutes • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">&copy; 2025 TicketDesk. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CompareZendesk;